import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging'
import { app, config } from '../config/firebase'
import { del, post } from './client'

/**
 * Web push, in the browser.
 *
 * Firebase Cloud Messaging rather than the raw Push API, because the rider app already
 * pushes through FCM and the server sends both the same way — one transport, one set of
 * tokens, one place where dead registrations are pruned. The alternative meant our own
 * VAPID key pair and a second sending path to keep correct.
 *
 * ⚠️ **Three things stop this working, none of them bugs:**
 *
 *   - **iOS Safari only delivers web push to a site added to the Home Screen** (16.4+).
 *     An iPhone customer browsing normally can subscribe to nothing. Apple's rule.
 *   - It needs **HTTPS**, or `localhost`. On a plain `http://` LAN address the service
 *     worker will not register at all.
 *   - The permission prompt can only be shown **once**. A person who dismisses it cannot
 *     be asked again from code, only through browser settings.
 */

/** The public half of the project's Web Push key pair. Firebase generates it; it is not secret. */
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY

let messagingPromise = null

/**
 * Register the worker, handing it the project the page itself was built against.
 *
 * The worker is served raw from `public/` and never sees the bundler, so it cannot read an
 * environment variable. It used to keep its own written-out copy of the config, and that
 * copy stayed pointed at the development project after the app moved to production —
 * tokens were minted on one project and the worker introduced itself as another, so every
 * background notification was dropped without a word. The URL is how the one value that
 * matters reaches it.
 *
 * `authDomain` is not passed: messaging has no use for it, and it is the one field that
 * legitimately differs between the page and Firebase.
 */
const registerWorker = () => {
  const settings = new URLSearchParams({
    apiKey: config.apiKey,
    projectId: config.projectId,
    messagingSenderId: config.messagingSenderId,
    appId: config.appId,
  })

  return navigator.serviceWorker.register(`/firebase-messaging-sw.js?${settings}`)
}

/**
 * `isSupported()` rather than sniffing for `Notification`. Firefox in private browsing and
 * every iOS browser before 16.4 have the object and cannot actually do this, so asking
 * Firebase whether *it* can work is the only answer that holds.
 */
const messagingIfPossible = async () => {
  if (!messagingPromise) {
    messagingPromise = isSupported()
      .then((ok) => (ok ? getMessaging(app) : null))
      .catch(() => null)
  }

  return messagingPromise
}

export const pushSupported = () => messagingIfPossible().then(Boolean)

/** What the browser currently thinks, without asking it anything. */
export const pushPermission = () =>
  typeof Notification === 'undefined' ? 'unsupported' : Notification.permission

/**
 * Ask, subscribe, and tell the server.
 *
 * Only ever called from a **click**. Browsers increasingly refuse a permission prompt that
 * was not provoked by one, and a prompt fired on page load is the one everybody blocks
 * without reading — after which they cannot be asked again.
 *
 * Returns the reason it did not work rather than throwing, because every failure here is
 * something to explain rather than an error: notifications are a nicety and no screen
 * should break for want of them.
 */
export async function enablePush() {
  if (!VAPID_KEY) return { ok: false, reason: 'not_configured' }

  const messaging = await messagingIfPossible()
  if (!messaging) return { ok: false, reason: 'unsupported' }

  const permission = await Notification.requestPermission().catch(() => 'default')
  if (permission !== 'granted') return { ok: false, reason: permission }

  return subscribe(messaging)
}

/**
 * Mint a token and hand it to the server.
 *
 * Split out of `enablePush` because it is the half that has to run **again**, and often —
 * see `syncPush`. Asking for permission is a once-ever event; registering the token that
 * permission produces is not.
 */
async function subscribe(messaging) {
  try {
    // Registered explicitly rather than left to Firebase, so a failure to register is a
    // reason we can report instead of a token call that hangs — and so the worker is
    // handed the right project, which is the whole of `registerWorker`.
    const registration = await registerWorker()

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    })

    if (!token) return { ok: false, reason: 'no_token' }

    await post('/api/push/subscribe', { token, platform: 'web' })

    // Kept so unsubscribing can name the token to remove. The server holds several per
    // account — one per browser — and must not drop somebody's other device.
    localStorage.setItem('machi.push.token', token)

    return { ok: true, token }
  } catch (error) {
    return { ok: false, reason: 'failed', error }
  }
}

/**
 * Re-register this browser, silently, on every sign-in.
 *
 * **The bug this exists to fix.** `enablePush` used to be the only thing that ever called
 * `/api/push/subscribe`, and it only runs from a click on `NotifyToggle` — which renders no
 * button at all once `Notification.permission` is `granted`. So the server was told about a
 * browser exactly once, ever, and every reason a registration goes stale was permanent:
 *
 *   - **FCM rotates the token.** A service worker update, evicted storage, cleared site
 *     data, or a long enough gap between visits mints a new one. The server's copy then
 *     fails to send, is pruned as dead, and nothing re-registers it. Push worked, then
 *     quietly stopped, while the screen still read "Notifications are on for this browser."
 *   - **Signing out and back in.** The token is bound to whichever account was signed in
 *     when it was registered. Permission is still granted afterwards, so no button appears
 *     and the token stays attached to the previous session.
 *   - **A failed first attempt.** If that one subscribe call 401'd on an expired session,
 *     permission had already been granted — so the toggle went quiet forever and there was
 *     no way to retry from inside the app.
 *
 * Called with a live session, because the endpoint identifies the account by the caller's
 * own token. Cheap and idempotent: the server keys devices by the token string, so
 * re-sending an unchanged one rewrites the same entry rather than adding a second.
 *
 * Silent about everything. Nobody asked for this to happen and no screen should react to
 * it failing.
 */
export async function syncPush() {
  if (!VAPID_KEY) return { ok: false, reason: 'not_configured' }

  // Never prompts. `getToken` would ask by itself if permission had not been decided, and
  // a prompt nobody provoked is the one everybody dismisses — after which they cannot be
  // asked again from code.
  if (pushPermission() !== 'granted') return { ok: false, reason: 'not_granted' }

  const messaging = await messagingIfPossible()
  if (!messaging) return { ok: false, reason: 'unsupported' }

  return subscribe(messaging)
}

/**
 * Stop notifying this browser.
 *
 * Must run **before** signing out, since the endpoint identifies the subscription by the
 * caller's own session. After sign-out there is no way to say which one to remove, and the
 * next person to use this browser inherits somebody else's notifications.
 */
export async function disablePush() {
  const token = localStorage.getItem('machi.push.token')

  if (!token) return { ok: true, reason: 'not_subscribed' }

  try {
    // The token goes in the query, not a body. `del` takes request options rather than a
    // payload, and a `DELETE` carrying a body is dropped by some proxies — the endpoint
    // accepts either, so the safer one is used.
    await del(`/api/push/subscribe?token=${encodeURIComponent(token)}`)
  } catch {
    // The subscription is stale on the server, which prunes dead tokens when a send
    // fails. Not worth blocking a sign-out over.
  }

  localStorage.removeItem('machi.push.token')

  return { ok: true }
}

/**
 * A notification arriving while the tab is in front.
 *
 * The service worker does not run for these — a foreground message is delivered to the
 * page instead, and if nothing handles it the person sees nothing at all while looking
 * straight at the app. The caller decides what to show.
 */
export async function onPushWhileOpen(handler) {
  const messaging = await messagingIfPossible()

  if (!messaging) return () => {}

  return onMessage(messaging, (payload) => {
    handler({
      title: payload.notification?.title ?? '',
      body: payload.notification?.body ?? '',
      url: payload.data?.url ?? '/',
      data: payload.data ?? {},
    })
  })
}

/**
 * Show the notifications that arrive while the tab is in front.
 *
 * **The gap this closes.** FCM does not run the service worker for a message that lands
 * while the page is focused — it hands it to the page instead, and `onBackgroundMessage`
 * never fires. Nothing in this app was listening, so a customer sitting on the screen for
 * the very order they were tracking saw nothing at all, which reads as push being broken
 * rather than as a message being delivered somewhere nobody looked.
 *
 * Drawn through the service worker's registration rather than `new Notification()`: the
 * constructor is unavailable on Android, and going through the registration means a
 * foreground notification carries the same tag and the same click handling as a background
 * one. Same collapsing per order, same tap-to-open behaviour, one code path to reason about.
 *
 * Silent by design when permission was never granted — `navigator.serviceWorker.ready`
 * never settles if no worker was ever registered, and this must not hang a page for
 * somebody who declined notifications.
 */
export async function showForegroundNotifications() {
  // `serviceWorker` is absent on a plain http:// origin, where `ready` would not reject so
  // much as fail to exist. Called at startup and never awaited, anything thrown here is an
  // unhandled rejection in a console nobody reads.
  if (!navigator.serviceWorker) return () => {}
  if (pushPermission() !== 'granted') return () => {}

  const registration = await navigator.serviceWorker.ready.catch(() => null)

  if (!registration) return () => {}

  return onPushWhileOpen(({ title, body, url, data }) => {
    if (!title) return

    registration.showNotification(title, {
      body,
      icon: '/favicon-192.png',
      badge: '/favicon-32.png',
      data: { url },
      tag: data.orderId || 'machi-express',
      renotify: true,
    })
  })
}
